"use client";

import { Heart, Loader2 } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { ProductCard } from "@/components/product/product-card";
import { DashboardShell } from "@/components/dashboard/dashboard-shell";
import { useWishlistStore } from "@/components/providers/wishlist-store";
import { serializeProduct } from "@/lib/serialize-product";
import type { Product } from "@/types";

export default function WishlistPage() {
  const storeItems = useWishlistStore((s) => s.items);
  const [serverItems, setServerItems] = useState<Product[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/wishlist")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data.items)) {
          setServerItems(
            data.items.map((row: { product: Parameters<typeof serializeProduct>[0] }) =>
              serializeProduct(row.product)
            )
          );
        } else {
          setServerItems(null);
        }
      })
      .catch(() => {
        setServerItems(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Prefer server state when available (user logged in), fall back to local store
  const items = serverItems ?? storeItems;

  return (
    <DashboardShell title="Your Wishlist">
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading wishlist…
        </div>
      ) : items.length === 0 ? (
        <div className="amazon-card py-12 text-center">
          <Heart className="mx-auto h-10 w-10 text-slate-400" />
          <p className="mt-3 font-bold">Your wishlist is empty</p>
          <p className="mt-1 text-sm text-slate-500">Save items you love for later.</p>
          <Link href="/search" className="amazon-btn-primary mt-5 inline-flex">
            Discover products
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
